import { useUser } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { MessageCircle, Paperclip } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useConversations } from "@/lib/hooks/use-conversations";
import type { Conversation } from "@/lib/api/services/chat.service";

interface RecentMessagesCardProps {
  chatRoute?: string;
  maxConversations?: number;
  className?: string;
}

export function RecentMessagesCard({
  chatRoute = "/genius/chat",
  maxConversations = 4,
  className,
}: RecentMessagesCardProps) {
  const { user } = useUser();
  const { conversations, isLoading } = useConversations();

  // Get other participant info from conversation
  const getOtherParticipant = (conversation: Conversation) => {
    return conversation.participants.find((p) => p.user_id !== user?.id);
  };

  // Format last message time
  const formatLastMessageTime = (timestamp: string | null): string => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    // For older messages, show date
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Get recent conversations sorted by last_message_at (most recent first)
  const recentConversations = conversations
    .filter((conv) => conv.last_message_at !== null)
    .sort((a, b) => {
      const timeA = a.last_message_at
        ? new Date(a.last_message_at).getTime()
        : 0;
      const timeB = b.last_message_at
        ? new Date(b.last_message_at).getTime()
        : 0;
      return timeB - timeA;
    })
    .slice(0, maxConversations);

  return (
    <div
      className={`bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 p-5 sm:p-6 ${className || ""}`}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-900">Recent Messages</h3>
        <Link
          to={chatRoute}
          className="text-sm text-[#4669ed] hover:underline"
        >
          View All
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-3 rounded-xl bg-slate-50"
            >
              <Skeleton className="w-10 h-10 rounded-lg" />
              <div className="flex-1 min-w-0">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          ))}
        </div>
      ) : recentConversations.length > 0 ? (
        <div className="space-y-3">
          {recentConversations.map((conversation) => {
            const other = getOtherParticipant(conversation);
            const userName = other?.display_name || null;
            const profilePictureUrl = other?.profile_picture_url || null;
            const participant = conversation.participants.find(
              (p) => p.user_id === user?.id
            );
            const unread = participant?.unread_count || 0;

            const initials = userName
              ?.split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
              .slice(0, 2) || "U";

            return (
              <Link
                key={conversation.id}
                to={`${chatRoute}?conversationId=${conversation.id}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <Avatar className="h-10 w-10 flex-shrink-0">
                  {profilePictureUrl && (
                    <AvatarImage
                      src={profilePictureUrl}
                      alt={userName || "User"}
                    />
                  )}
                  <AvatarFallback className="font-semibold bg-slate-200 text-slate-600">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <p className="text-sm font-medium text-slate-900 line-clamp-1">
                      {userName ||
                        (other?.role === "genius" ? "Freelancer" : "Client")}
                    </p>
                    {conversation.last_message_at && (
                      <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">
                        {formatLastMessageTime(conversation.last_message_at)}
                      </span>
                    )}
                  </div>
                  {conversation.last_message_preview ? (
                    conversation.last_message_preview.startsWith("📎") ? (
                      <div className="text-xs truncate text-slate-500 flex items-center gap-1.5">
                        <Paperclip className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">
                          {conversation.last_message_preview.replace(
                            /^📎\s*/,
                            ""
                          )}
                        </span>
                      </div>
                    ) : (
                      <p className="text-xs truncate text-slate-500">
                        {conversation.last_message_preview}
                      </p>
                    )
                  ) : (
                    <p className="text-xs truncate text-slate-500">
                      No messages yet
                    </p>
                  )}
                </div>
                {unread > 0 && (
                  <span className="bg-[#4669ed] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                    {unread}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6">
          <MessageCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500 mb-3">No messages yet</p>
        </div>
      )}
    </div>
  );
}
